import { BREAKPOINTS, PHYSICAL_LIMITS, DEFAULT_MATRIX_COLUMN_GAP_PX, type Breakpoint } from './constants';
import {
  distributeLeftoverSpace,
  inferSuperstructure,
  resolveToleranceAtBreakpoint,
  resolvePackCols,
  type PackedFieldSlot,
} from './inference';
import { extractRawColumnsAtBreakpoints } from './quantize';
import type { CompiledCell, CompiledMatrix, MatrixFieldInput, SolverInput, SolverOptions } from './types';

const ROW_TRACKS_PER_LOGICAL_ROW = 1;

const DEFAULT_OPTIONS: SolverOptions = {
  preserveHiddenFootprint: true,
  responsiveDownshift: true,
  columnGapPx: DEFAULT_MATRIX_COLUMN_GAP_PX,
};

/**
 * Auto-derive `idealCols` from the widest logical row group's `minCols` sum.
 * Groups are delimited by `row-break` and `group-hint` markers.
 * `row-break` = visual row break (titled sections) — forces a row flush in the packer.
 * `group-hint` = structural-only boundary (block edges) — tells derivation where
 * groups end but the packer ignores it, so fields flow freely across blocks.
 *
 * This mirrors the HTML prototype's `schema.forEach(group => sum(minCols))`.
 */
function deriveIdealCols(fields: MatrixFieldInput[]): number {
  let idealCols = 0;
  let rowSum = 0;
  let groupIndex = 0;

  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    if (field.kind === 'row-break' || field.kind === 'group-hint') {
      if (rowSum > 0) {
        idealCols = Math.max(idealCols, rowSum);
        groupIndex++;
      }
      rowSum = 0;
      continue;
    }
    const forcesBreak = field.tolerance.startNewRow === true && i > 0;
    if (forcesBreak && rowSum > 0) {
      idealCols = Math.max(idealCols, rowSum);
      groupIndex++;
      rowSum = field.tolerance.minCols;
    } else {
      rowSum += field.tolerance.minCols;
    }
  }

  if (rowSum > 0) {
    idealCols = Math.max(idealCols, rowSum);
    groupIndex++;
  }

  void groupIndex;
  return Math.max(1, idealCols);
}

export function solveMatrix(input: SolverInput): Record<Breakpoint, CompiledMatrix> {
  const opts: SolverOptions = { ...DEFAULT_OPTIONS, ...input.options };
  const rawCols = extractRawColumnsAtBreakpoints(input.row.columns);

  if (opts.containerWidth != null && opts.containerWidth > 0) {
    const single = solveAtBreakpoint(input, 'base', rawCols, opts);
    return Object.fromEntries(BREAKPOINTS.map((bp) => [bp, single])) as Record<Breakpoint, CompiledMatrix>;
  }

  const result = {} as Record<Breakpoint, CompiledMatrix>;
  for (const bp of BREAKPOINTS) {
    result[bp] = solveAtBreakpoint(input, bp, rawCols, opts);
  }
  return result;
}

function solveAtBreakpoint(
  input: SolverInput,
  bp: Breakpoint,
  rawCols: Record<Breakpoint, number> | null,
  opts: SolverOptions,
): CompiledMatrix {
  const fields = [...input.row.fields].sort((a, b) => a.order - b.order);
  const gapPx = opts.columnGapPx ?? DEFAULT_MATRIX_COLUMN_GAP_PX;

  const declaredCols = rawCols?.[bp] ?? null;

  const idealCols = declaredCols ?? deriveIdealCols(fields);

  const columnsAtBp: Record<Breakpoint, number> = rawCols ?? {
    base: idealCols,
    sm: idealCols,
    md: idealCols,
    lg: idealCols,
    xl: idealCols,
  };

  const colCount =
    opts.containerWidth != null && opts.containerWidth > 0
      ? inferSuperstructure({
          idealCols,
          containerWidth: opts.containerWidth,
          columnGapPx: gapPx,
          fields,
          breakpoint: bp,
          columnsAtBp,
        })
      : idealCols;

  const finalColWidth =
    opts.containerWidth != null && opts.containerWidth > 0 && colCount > 0
      ? (opts.containerWidth - Math.max(0, colCount - 1) * gapPx) / colCount
      : undefined;

  let rowIndex = 1;
  let currentRow: PackedFieldSlot[] = [];
  let currentUsedCols = 0;
  const cells: CompiledCell[] = [];

  const flushRow = (): void => {
    if (currentRow.length === 0) return;

    const leftover = colCount - currentUsedCols;
    const packedRow =
      leftover > 0
        ? distributeLeftoverSpace(currentRow, leftover, finalColWidth, gapPx)
        : currentRow;

    let cursor = 1;
    for (const slot of packedRow) {
      const field = fields.find((f) => f.id === slot.id);
      if (!field) continue;

      const isVisible = input.visibility[bp]?.[field.id] ?? field.visible[bp] ?? true;
      if (!isVisible && !opts.preserveHiddenFootprint) continue;

      cells.push({
        id: slot.id,
        rowIndex,
        gridColumnStart: cursor,
        gridSpan: slot.actualCols,
        gridRowStart: (rowIndex - 1) * ROW_TRACKS_PER_LOGICAL_ROW + 1,
        isVirtualPlaceholder: !isVisible,
        breakpoint: bp,
        minWidthPx: slot.minWidthPx,
        maxWidthPx: slot.maxWidthPx,
      });

      cursor += slot.actualCols;
    }

    rowIndex++;
    currentRow = [];
    currentUsedCols = 0;
  };

  for (let fieldIndex = 0; fieldIndex < fields.length; fieldIndex++) {
    const field = fields[fieldIndex];

    if (field.kind === 'row-break') {
      flushRow();
      cells.push({
        id: field.id,
        rowIndex,
        gridColumnStart: 1,
        gridSpan: colCount,
        gridRowStart: (rowIndex - 1) * ROW_TRACKS_PER_LOGICAL_ROW + 1,
        isVirtualPlaceholder: false,
        breakpoint: bp,
      });
      rowIndex++;
      continue;
    }

    if (field.kind === 'group-hint') {
      continue;
    }

    const tol = resolveToleranceAtBreakpoint(field, bp, columnsAtBp);
    const minCols = Math.max(PHYSICAL_LIMITS.minSpan, Math.min(tol.minCols, colCount));
    const maxCols = Math.max(minCols, Math.min(tol.maxCols, colCount));

    const hasForceRowBreak = field.tolerance.startNewRow === true && currentRow.length > 0 && colCount > 1;
    const structuralFit = (currentUsedCols + minCols <= colCount) && !hasForceRowBreak;

    if (structuralFit) {
      const nextTol =
        fieldIndex + 1 < fields.length
          ? resolveToleranceAtBreakpoint(fields[fieldIndex + 1], bp, columnsAtBp)
          : undefined;
      const nextFieldMinCols = nextTol
        ? Math.max(PHYSICAL_LIMITS.minSpan, Math.min(nextTol.minCols, colCount))
        : 0;

      const packCols = resolvePackCols(
        minCols,
        maxCols,
        currentUsedCols,
        colCount,
        tol.minWidthPx,
        nextFieldMinCols,
      );

      currentRow.push({
        id: field.id,
        actualCols: packCols,
        maxCols,
        minWidthPx: tol.minWidthPx,
        maxWidthPx: tol.maxWidthPx,
      });
      currentUsedCols += packCols;
    } else {
      flushRow();

      currentRow.push({
        id: field.id,
        actualCols: minCols,
        maxCols,
        minWidthPx: tol.minWidthPx,
        maxWidthPx: tol.maxWidthPx,
      });
      currentUsedCols = minCols;
    }

    if (rowIndex > PHYSICAL_LIMITS.maxRows) {
      throw new Error(`Matrix solver exceeded maxRows (${PHYSICAL_LIMITS.maxRows}) at breakpoint ${bp}`);
    }
  }

  flushRow();

  return {
    columnCount: colCount,
    breakpoint: bp,
    rows: Math.max(1, rowIndex - 1),
    cells,
  };
}
